import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { bugReportsAPI, userReportsAPI } from '../services/api';
import './BugReports.css';

interface BugReport {
  id: number;
  user_id: number | null;
  first_name?: string;
  last_name?: string;
  email?: string;
  description: string;
  page_url: string | null;
  browser_info: string | null;
  screenshot_url: string | null;
  status: 'new' | 'in_progress' | 'resolved' | 'closed';
  created_at: string;
  resolved_at: string | null;
  admin_notes: string | null;
}

interface UserReport {
  id: number;
  reporter_id: number;
  reported_user_id: number;
  reporter_first_name: string;
  reporter_last_name: string;
  reporter_email: string;
  reported_first_name: string;
  reported_last_name: string;
  reported_email: string;
  reported_profile_photo: string | null;
  reported_phone: string | null;
  reason: string;
  description: string;
  status: 'new' | 'investigating' | 'resolved' | 'dismissed';
  admin_notes: string | null;
  created_at: string;
  resolved_at: string | null;
}

type ActiveTab = 'bugs' | 'reports';

const BugReports: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'reports' ? 'reports' : 'bugs';
  const [activeTab, setActiveTab] = useState<ActiveTab>(initialTab);

  // Bug reports state
  const [reports, setReports] = useState<BugReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [selectedReport, setSelectedReport] = useState<BugReport | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [promptReportId, setPromptReportId] = useState<number | null>(null);
  const [promptCopied, setPromptCopied] = useState(false);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState('');

  // User reports state
  const [userReports, setUserReports] = useState<UserReport[]>([]);
  const [userReportsLoading, setUserReportsLoading] = useState(true);
  const [userReportsError, setUserReportsError] = useState('');
  const [userReportsFilter, setUserReportsFilter] = useState<string>('');
  const [selectedUserReport, setSelectedUserReport] = useState<UserReport | null>(null);
  const [userReportAdminNotes, setUserReportAdminNotes] = useState('');
  const [updatingUserReportStatus, setUpdatingUserReportStatus] = useState(false);

  // Bug reports fetch
  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const data = await bugReportsAPI.getAll(filterStatus || undefined);
      setReports(data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  // User reports fetch
  const fetchUserReports = useCallback(async () => {
    try {
      setUserReportsLoading(true);
      const data = await userReportsAPI.getAll(userReportsFilter || undefined);
      setUserReports(data);
      setUserReportsError('');
    } catch (err: any) {
      setUserReportsError(err.response?.data?.message || 'Erreur lors du chargement des signalements');
    } finally {
      setUserReportsLoading(false);
    }
  }, [userReportsFilter]);

  useEffect(() => {
    if (activeTab === 'bugs') {
      fetchReports();
    } else {
      fetchUserReports();
    }
  }, [activeTab, fetchReports, fetchUserReports]);

  // Bug report handlers
  const handleStatusUpdate = async (reportId: number, newStatus: string) => {
    try {
      setUpdatingStatus(true);
      await bugReportsAPI.updateStatus(reportId, newStatus, adminNotes || undefined);
      await fetchReports();
      setSelectedReport(null);
      setAdminNotes('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDelete = async (reportId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce rapport ?')) {
      return;
    }
    try {
      await bugReportsAPI.delete(reportId);
      await fetchReports();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  // User report handlers
  const handleUserReportStatusUpdate = async (reportId: number, newStatus: string) => {
    try {
      setUpdatingUserReportStatus(true);
      await userReportsAPI.updateStatus(reportId, newStatus, userReportAdminNotes || undefined);
      await fetchUserReports();
      setSelectedUserReport(null);
      setUserReportAdminNotes('');
    } catch (err: any) {
      setUserReportsError(err.response?.data?.message || 'Erreur lors de la mise à jour');
    } finally {
      setUpdatingUserReportStatus(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return '#f44336';
      case 'in_progress':
      case 'investigating': return '#ff9800';
      case 'resolved': return '#4caf50';
      case 'closed':
      case 'dismissed': return '#9e9e9e';
      default: return '#333';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'new': return 'Nouveau';
      case 'in_progress': return 'En cours';
      case 'investigating': return 'En investigation';
      case 'resolved': return 'Résolu';
      case 'closed': return 'Fermé';
      case 'dismissed': return 'Rejeté';
      default: return status;
    }
  };

  const getReasonLabel = (reason: string) => {
    switch (reason) {
      case 'harassment': return 'Harcèlement';
      case 'inappropriate_content': return 'Contenu inapproprié';
      case 'fake_profile': return 'Faux profil';
      case 'child_safety': return 'Sécurité enfants';
      case 'spam': return 'Spam';
      case 'other': return 'Autre';
      default: return reason;
    }
  };

  const getPhotoUrl = (photoPath?: string | null) => {
    if (!photoPath) return null;
    if (photoPath.startsWith('http')) return photoPath;
    return `${process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:5000'}${photoPath}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const generateClaudePrompt = (report: BugReport): string => {
    const lines = [
      `# Rapport de bug #${report.id}`,
      '',
      '## Description du problème',
      report.description,
      '',
    ];
    if (report.page_url) {
      lines.push('## Page concernée', report.page_url, '');
    }
    if (report.browser_info) {
      lines.push('## Navigateur', report.browser_info, '');
    }
    if (report.admin_notes) {
      lines.push('## Notes admin', report.admin_notes, '');
    }
    lines.push('## Contexte projet');
    lines.push('- Application: Bob Map (bob-map.com)');
    lines.push('- Stack: React + TypeScript (frontend), Node.js + Express (backend), PostgreSQL');
    lines.push('- Voir CLAUDE.md pour la structure complète du projet');
    lines.push('');
    lines.push('## Demande');
    lines.push('Aide-moi à investiguer et résoudre ce bug. Commence par analyser le problème décrit et propose une approche pour le corriger.');
    return lines.join('\n');
  };

  const handleOpenPrompt = (report: BugReport) => {
    const prompt = generateClaudePrompt(report);
    setCurrentPrompt(prompt);
    setPromptReportId(report.id);
    setShowPromptModal(true);
  };

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(currentPrompt);
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy prompt:', err);
    }
  };

  const handleClosePromptModal = () => {
    setShowPromptModal(false);
    setCurrentPrompt('');
    setPromptReportId(null);
    setPromptCopied(false);
  };

  // Admin actions on reported user
  const handleBanUser = async (userId: number, userName: string) => {
    if (!window.confirm(`Bannir ${userName} ? Son mot de passe sera invalidé et il ne pourra plus se connecter.`)) return;
    try {
      const result = await userReportsAPI.banUser(userId, `Banni suite au signalement #${selectedUserReport?.id}`);
      alert(result.message);
      await fetchUserReports();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erreur lors du bannissement');
    }
  };

  const handleDeleteUserAccount = async (userId: number, userName: string) => {
    if (!window.confirm(`SUPPRIMER DEFINITIVEMENT le compte de ${userName} ? Cette action est IRREVERSIBLE.`)) return;
    if (!window.confirm(`Derniere confirmation : supprimer ${userName} et toutes ses donnees ?`)) return;
    try {
      const result = await userReportsAPI.deleteUserAccount(userId);
      alert(result.message);
      await fetchUserReports();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  // Count new user reports for badge
  const newUserReportsCount = userReports.filter(r => r.status === 'new').length;

  return (
    <div className="bug-reports-page">
      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'bugs' ? 'active' : ''}`}
          onClick={() => setActiveTab('bugs')}
        >
          🐛 Bugs
          {reports.filter(r => r.status === 'new').length > 0 && (
            <span className="tab-badge">{reports.filter(r => r.status === 'new').length}</span>
          )}
        </button>
        <button
          className={`admin-tab ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('reports')}
        >
          ⚠️ Signalements
          {newUserReportsCount > 0 && (
            <span className="tab-badge tab-badge-danger">{newUserReportsCount}</span>
          )}
        </button>
      </div>

      {/* ===================== BUG REPORTS TAB ===================== */}
      {activeTab === 'bugs' && (
        <>
          <div className="bug-reports-header">
            <h1>🐛 Rapports de bugs</h1>
            <div className="bug-reports-stats">
              <span className="stat">Total: {reports.length}</span>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="bug-reports-filters">
            <label>
              Filtrer par statut:
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="">Tous</option>
                <option value="new">Nouveaux</option>
                <option value="in_progress">En cours</option>
                <option value="resolved">Résolus</option>
                <option value="closed">Fermés</option>
              </select>
            </label>
          </div>

          {loading ? (
            <div className="loading">Chargement des rapports...</div>
          ) : (
            <div className="bug-reports-list">
              {reports.length === 0 ? (
                <div className="no-reports">Aucun rapport trouvé</div>
              ) : (
                reports.map((report) => (
                  <div key={report.id} className="bug-report-card">
                    <div className="report-header">
                      <div className="report-id">#{report.id}</div>
                      <div className="report-status" style={{ backgroundColor: getStatusColor(report.status) }}>
                        {getStatusLabel(report.status)}
                      </div>
                    </div>

                    <div className="report-user">
                      {report.first_name && report.last_name ? (
                        <>
                          <strong>{report.first_name} {report.last_name}</strong>
                          <span className="report-email">{report.email}</span>
                        </>
                      ) : (
                        <span className="anonymous">Utilisateur anonyme</span>
                      )}
                    </div>

                    <div className="report-description">{report.description}</div>

                    {report.page_url && (
                      <div className="report-meta"><strong>Page:</strong> {report.page_url}</div>
                    )}
                    {report.browser_info && (
                      <div className="report-meta"><strong>Navigateur:</strong> {report.browser_info}</div>
                    )}
                    {report.screenshot_url && (
                      <div className="report-screenshot">
                        <strong>Capture d'écran:</strong>
                        <a href={`${process.env.REACT_APP_API_URL?.replace(/\/api$/, '')}${report.screenshot_url}`} target="_blank" rel="noopener noreferrer">
                          <img
                            src={`${process.env.REACT_APP_API_URL?.replace(/\/api$/, '')}${report.screenshot_url}`}
                            alt="Bug screenshot"
                          />
                        </a>
                      </div>
                    )}
                    {report.admin_notes && (
                      <div className="report-admin-notes"><strong>Notes admin:</strong> {report.admin_notes}</div>
                    )}

                    <div className="report-date">Signalé le {formatDate(report.created_at)}</div>
                    {report.resolved_at && (
                      <div className="report-resolved">Résolu le {formatDate(report.resolved_at)}</div>
                    )}

                    <div className="report-actions">
                      <button className="btn-edit" onClick={() => { setSelectedReport(report); setAdminNotes(report.admin_notes || ''); }}>
                        Modifier le statut
                      </button>
                      <button className="btn-claude" onClick={() => handleOpenPrompt(report)}>
                        Prompt Claude
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(report.id)}>
                        Supprimer
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}

      {/* ===================== USER REPORTS TAB ===================== */}
      {activeTab === 'reports' && (
        <>
          <div className="bug-reports-header">
            <h1>⚠️ Signalements utilisateurs</h1>
            <div className="bug-reports-stats">
              <span className="stat">Total: {userReports.length}</span>
            </div>
          </div>

          {userReportsError && <div className="error-message">{userReportsError}</div>}

          <div className="bug-reports-filters">
            <label>
              Filtrer par statut:
              <select value={userReportsFilter} onChange={(e) => setUserReportsFilter(e.target.value)}>
                <option value="">Tous</option>
                <option value="new">Nouveaux</option>
                <option value="investigating">En investigation</option>
                <option value="resolved">Résolus</option>
                <option value="dismissed">Rejetés</option>
              </select>
            </label>
          </div>

          {userReportsLoading ? (
            <div className="loading">Chargement des signalements...</div>
          ) : (
            <div className="bug-reports-list">
              {userReports.length === 0 ? (
                <div className="no-reports">Aucun signalement trouvé</div>
              ) : (
                userReports.map((report) => (
                  <div
                    key={report.id}
                    className={`bug-report-card ${report.reason === 'child_safety' ? 'card-urgent' : ''}`}
                  >
                    <div className="report-header">
                      <div className="report-id">#{report.id}</div>
                      <div className="report-badges">
                        <span
                          className="report-reason-badge"
                          style={{
                            backgroundColor: report.reason === 'child_safety' ? '#d32f2f' : '#ff9800',
                          }}
                        >
                          {getReasonLabel(report.reason)}
                        </span>
                        <div className="report-status" style={{ backgroundColor: getStatusColor(report.status) }}>
                          {getStatusLabel(report.status)}
                        </div>
                      </div>
                    </div>

                    <div className="report-users-info">
                      <div className="report-user-row">
                        <span className="report-user-label">Signalé par:</span>
                        <strong>{report.reporter_first_name} {report.reporter_last_name}</strong>
                        <span className="report-email">{report.reporter_email}</span>
                      </div>
                      <div className="reported-user-card">
                        <span className="report-user-label">Utilisateur signalé:</span>
                        <div className="reported-user-info">
                          {report.reported_profile_photo ? (
                            <img
                              src={getPhotoUrl(report.reported_profile_photo) || undefined}
                              alt={`${report.reported_first_name} ${report.reported_last_name}`}
                              className="reported-user-photo"
                            />
                          ) : (
                            <div className="reported-user-photo-placeholder">
                              {report.reported_first_name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="reported-user-details">
                            <strong>{report.reported_first_name} {report.reported_last_name}</strong>
                            <span className="report-email">{report.reported_email}</span>
                            {report.reported_phone && (
                              <span className="reported-user-phone">{report.reported_phone}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="report-description">{report.description}</div>

                    {report.admin_notes && (
                      <div className="report-admin-notes"><strong>Notes admin:</strong> {report.admin_notes}</div>
                    )}

                    <div className="report-date">Signalé le {formatDate(report.created_at)}</div>
                    {report.resolved_at && (
                      <div className="report-resolved">Traité le {formatDate(report.resolved_at)}</div>
                    )}

                    <div className="report-actions">
                      <button
                        className="btn-edit"
                        onClick={() => {
                          setSelectedUserReport(report);
                          setUserReportAdminNotes(report.admin_notes || '');
                        }}
                      >
                        Modifier le statut
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}

      {/* Bug Report Status Update Modal */}
      {selectedReport && (
        <div className="modal-overlay" onClick={() => setSelectedReport(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Modifier le statut - Rapport #{selectedReport.id}</h2>
            <div className="modal-content">
              <div className="form-group">
                <label>Notes administrateur:</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Ajouter des notes..."
                  rows={3}
                />
              </div>
              <div className="status-buttons">
                <button onClick={() => handleStatusUpdate(selectedReport.id, 'new')} disabled={updatingStatus} className="status-btn status-new">Nouveau</button>
                <button onClick={() => handleStatusUpdate(selectedReport.id, 'in_progress')} disabled={updatingStatus} className="status-btn status-in-progress">En cours</button>
                <button onClick={() => handleStatusUpdate(selectedReport.id, 'resolved')} disabled={updatingStatus} className="status-btn status-resolved">Résolu</button>
                <button onClick={() => handleStatusUpdate(selectedReport.id, 'closed')} disabled={updatingStatus} className="status-btn status-closed">Fermé</button>
              </div>
              <button className="btn-cancel" onClick={() => setSelectedReport(null)} disabled={updatingStatus}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* User Report Status Update Modal */}
      {selectedUserReport && (
        <div className="modal-overlay" onClick={() => setSelectedUserReport(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Modifier le statut - Signalement #{selectedUserReport.id}</h2>
            <div className="modal-content">
              <div className="report-users-info" style={{ marginBottom: 16 }}>
                <p><strong>{getReasonLabel(selectedUserReport.reason)}</strong> — {selectedUserReport.reported_first_name} {selectedUserReport.reported_last_name}</p>
              </div>
              <div className="form-group">
                <label>Notes administrateur:</label>
                <textarea
                  value={userReportAdminNotes}
                  onChange={(e) => setUserReportAdminNotes(e.target.value)}
                  placeholder="Ajouter des notes (actions prises, résultat de l'investigation...)"
                  rows={3}
                />
              </div>
              <div className="status-buttons">
                <button onClick={() => handleUserReportStatusUpdate(selectedUserReport.id, 'new')} disabled={updatingUserReportStatus} className="status-btn status-new">Nouveau</button>
                <button onClick={() => handleUserReportStatusUpdate(selectedUserReport.id, 'investigating')} disabled={updatingUserReportStatus} className="status-btn status-in-progress">En investigation</button>
                <button onClick={() => handleUserReportStatusUpdate(selectedUserReport.id, 'resolved')} disabled={updatingUserReportStatus} className="status-btn status-resolved">Résolu</button>
                <button onClick={() => handleUserReportStatusUpdate(selectedUserReport.id, 'dismissed')} disabled={updatingUserReportStatus} className="status-btn status-closed">Rejeté</button>
              </div>

              <div className="admin-actions-section">
                <p className="admin-actions-label">Actions sur le compte signalé :</p>
                <div className="admin-actions-buttons">
                  <button
                    className="btn-ban"
                    onClick={() => handleBanUser(
                      selectedUserReport.reported_user_id,
                      `${selectedUserReport.reported_first_name} ${selectedUserReport.reported_last_name}`
                    )}
                  >
                    Bannir l'utilisateur
                  </button>
                  <button
                    className="btn-delete-account"
                    onClick={() => handleDeleteUserAccount(
                      selectedUserReport.reported_user_id,
                      `${selectedUserReport.reported_first_name} ${selectedUserReport.reported_last_name}`
                    )}
                  >
                    Supprimer le compte
                  </button>
                </div>
              </div>

              <button className="btn-cancel" onClick={() => setSelectedUserReport(null)} disabled={updatingUserReportStatus}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* Claude Prompt Modal */}
      {showPromptModal && (
        <div className="modal-overlay" onClick={handleClosePromptModal}>
          <div className="modal prompt-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Prompt Claude - Bug #{promptReportId}</h2>
            <div className="modal-content">
              <p className="prompt-instructions">Modifie le prompt si nécessaire, puis copie-le pour Claude:</p>
              <textarea className="prompt-textarea" value={currentPrompt} onChange={(e) => setCurrentPrompt(e.target.value)} rows={15} />
              <div className="prompt-actions">
                <button className={`btn-copy ${promptCopied ? 'copied' : ''}`} onClick={handleCopyPrompt}>
                  {promptCopied ? 'Copié !' : 'Copier le prompt'}
                </button>
                <button className="btn-cancel" onClick={handleClosePromptModal}>Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BugReports;
